import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { onboardingAPI } from '../../lib/onboardingAPI';

const Choice = ({ value, current, onPick, children, testid }) => (
  <button type="button" onClick={() => onPick(value)} data-testid={testid}
    className={`rounded-lg border px-4 py-2 text-sm transition-colors ${current === value ? 'border-primary bg-primary/10 text-foreground' : 'hover:bg-muted/50 text-foreground/80'}`}>
    {children}
  </button>
);

export default function GstBasForm({ clientId }) {
  const navigate = useNavigate();
  const [f, setF] = useState({ gst_registered: null, bas_cycle: null, payg_withholding: null, paygw_cycle: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    onboardingAPI.getSection(clientId, 'gst_bas').then((r) => setF((s) => ({ ...s, ...(r.data.response_data || {}) })))
      .catch(() => {}).finally(() => setLoading(false));
  }, [clientId]);

  const save = async (status) => {
    const miss = f.gst_registered === null ? "Please tell us if you're registered for GST" : f.gst_registered && !f.bas_cycle ? 'Please choose your BAS reporting cycle'
      : f.payg_withholding === null ? "Please tell us if you're registered for PAYG withholding" : f.payg_withholding && !f.paygw_cycle ? 'Please tell us how often you report withholding' : null;
    if (status === 'completed' && miss) {
      toast.error(miss);
      return;
    }
    setSaving(true);
    try {
      await onboardingAPI.saveSection(clientId, 'gst_bas', { response_data: { ...f, bas_cycle: f.gst_registered ? f.bas_cycle : null, paygw_cycle: f.payg_withholding ? f.paygw_cycle : null }, status });
      toast.success(status === 'completed' ? 'GST & PAYG details saved' : 'Saved — you can return any time');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;
  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/onboarding')} className="text-muted-foreground -ml-2"><ArrowLeft className="w-4 h-4 mr-2" /> Back to checklist</Button>
      <Card data-testid="gst-bas-form">
        <CardHeader>
          <CardTitle className="text-2xl text-foreground">GST, BAS &amp; PAYG withholding</CardTitle>
          <CardDescription>This tells us which activity statements (BAS / IAS) we need to prepare for you.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Registered for GST? *</Label>
            <div className="flex gap-2">
              <Choice value current={f.gst_registered} onPick={(v) => setF({ ...f, gst_registered: v })} testid="gst-yes">Yes</Choice>
              <Choice value={false} current={f.gst_registered} onPick={(v) => setF({ ...f, gst_registered: v, bas_cycle: null })} testid="gst-no">No</Choice>
            </div>
          </div>
          {f.gst_registered && (
            <div className="space-y-2">
              <Label>BAS reporting cycle *</Label>
              <div className="flex gap-2 flex-wrap">
                {[['monthly', 'Monthly'], ['quarterly', 'Quarterly'], ['annual', 'Annual']].map(([v, l]) => (
                  <Choice key={v} value={v} current={f.bas_cycle} onPick={(x) => setF({ ...f, bas_cycle: x })} testid={`bas-cycle-${v}`}>{l}</Choice>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Not sure? Check your last BAS or ask us — most small businesses report quarterly.</p>
            </div>
          )}
          <div className="space-y-2">
            <Label>Registered for PAYG withholding? *</Label>
            <p className="text-xs text-muted-foreground">You withhold tax from wages or other payments to employees.</p>
            <div className="flex gap-2">
              <Choice value current={f.payg_withholding} onPick={(v) => setF({ ...f, payg_withholding: v })} testid="paygw-yes">Yes</Choice>
              <Choice value={false} current={f.payg_withholding} onPick={(v) => setF({ ...f, payg_withholding: v, paygw_cycle: null })} testid="paygw-no">No</Choice>
            </div>
          </div>
          {f.payg_withholding && (
            <div className="space-y-2">
              <Label>How often do you report withholding? *</Label>
              <div className="flex gap-2 flex-wrap">
                {[['monthly', 'Monthly – separate IAS'], ['quarterly', 'Quarterly – on the BAS']].map(([v, l]) => (
                  <Choice key={v} value={v} current={f.paygw_cycle} onPick={(x) => setF({ ...f, paygw_cycle: x })} testid={`paygw-cycle-${v}`}>{l}</Choice>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t">
            <Button variant="outline" onClick={() => save('in_progress')} disabled={saving} data-testid="gst-save-later"><Save className="w-4 h-4 mr-2" /> Save &amp; return later</Button>
            <Button onClick={() => save('completed')} disabled={saving} data-testid="gst-complete"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark as complete</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
